import logoutIcon from '@/assets/icons/logout.svg';
import { iconMaskStyle } from '@/shared/lib/icon-mask';
import type { AccessibleOrganization } from '@/store';

import { GUARDIAN_ACCOUNT_MENU_PANEL_CLASS } from './constants';
import { UserAvatar } from '@/shared/components/UserAvatar';

function organizationInitialFromName(name: string): string {
  const t = name.trim();
  if (!t) {
    return '?';
  }
  return t.charAt(0).toUpperCase();
}

interface GuardianProviderPickerProps {
  sectionTitle: string;
  organizations: readonly AccessibleOrganization[];
  currentOrgId: string;
  switchingOrgId: string | null;
  onSelectOrganization: (organizationId: string) => Promise<void>;
}

function GuardianProviderPicker({
  sectionTitle,
  organizations,
  currentOrgId,
  switchingOrgId,
  onSelectOrganization,
}: GuardianProviderPickerProps) {
  return (
    <>
      <p className="px-3 pb-1.5 pt-0 text-[10px] font-bold uppercase tracking-[0.06em] text-ink-strong">
        {sectionTitle}
      </p>
      <ul className="max-h-48 overflow-y-auto px-1.5">
        {organizations.map((org) => {
          const isCurrent = org.id === currentOrgId;
          const busy = switchingOrgId !== null;
          return (
            <li key={org.id}>
              <button
                type="button"
                role="menuitem"
                aria-current={isCurrent ? 'true' : undefined}
                disabled={busy}
                onClick={() => {
                  void onSelectOrganization(org.id);
                }}
                className={`flex w-full items-center gap-2 rounded-lg px-1.5 py-2 text-left transition-colors ${
                  isCurrent ? 'bg-brand-50 ring-1 ring-inset ring-brand-100' : 'hover:bg-brand-50'
                } ${busy ? 'opacity-60' : ''}`}
              >
                <span
                  aria-hidden
                  className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold uppercase text-brand-800"
                >
                  {organizationInitialFromName(org.name)}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink-strong">
                  {org.name}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mx-2 my-1.5 h-px bg-stroke" />
    </>
  );
}

export interface GuardianAccountMenuProps {
  ariaLabel: string;
  providerSectionTitle: string;
  signOutCta: string;
  organizations: readonly AccessibleOrganization[];
  showProviderPicker: boolean;
  currentOrgId: string;
  switchingOrgId: string | null;
  displayName: string;
  userEmail: string;
  avatarUrl?: string;
  onSelectOrganization: (organizationId: string) => Promise<void>;
  onSignOut: () => void;
}

/** Guardian portal: switch provider, then identity block, then sign out. */
export function GuardianAccountMenu({
  ariaLabel,
  providerSectionTitle,
  signOutCta,
  organizations,
  showProviderPicker,
  currentOrgId,
  switchingOrgId,
  displayName,
  userEmail,
  avatarUrl,
  onSelectOrganization,
  onSignOut,
}: GuardianAccountMenuProps) {
  return (
    <div role="menu" aria-label={ariaLabel} className={GUARDIAN_ACCOUNT_MENU_PANEL_CLASS}>
      {showProviderPicker ? (
        <GuardianProviderPicker
          sectionTitle={providerSectionTitle}
          organizations={organizations}
          currentOrgId={currentOrgId}
          switchingOrgId={switchingOrgId}
          onSelectOrganization={onSelectOrganization}
        />
      ) : null}
      <div className="flex items-center gap-2.5 px-3 py-1.5">
        <UserAvatar
          name={displayName}
          avatarUrl={avatarUrl}
          sizeClassName="size-8"
          textClassName="text-[10px]"
          priority
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-ink-strong">{displayName}</p>
          {userEmail ? (
            <p className="mt-0.5 truncate text-[11px] text-ink-muted/80">{userEmail}</p>
          ) : null}
        </div>
      </div>
      <div className="px-1.5 pb-0.5 pt-0.5">
        <button
          type="button"
          role="menuitem"
          onClick={onSignOut}
          className="flex w-full items-center gap-2 rounded-lg px-1.5 py-2 text-left text-[13px] font-medium text-ink-strong transition-colors hover:bg-brand-50"
        >
          <span
            aria-hidden
            className="block size-3.5 shrink-0"
            style={iconMaskStyle(logoutIcon)}
          />
          <span>{signOutCta}</span>
        </button>
      </div>
    </div>
  );
}
